import React from 'react';
import { useIntl } from 'react-intl';
import { faRecordVinyl } from '@fortawesome/pro-light-svg-icons/faRecordVinyl';
import { TypedMemo } from '../../../common/types';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { ErrorAPIResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { SMLEmptyState } from '../../components/SMLEmptyState/SMLEmptyState';
import { SMLButton } from '../../components/primitives/SMLButton';
import { Panel } from '../../components/primitives/Panel';
import { releaseMessages } from './releaseMessages';

interface ReleaseNotFoundProps {
  /** The failed read, when there is one. Without it the song is not in the catalog of this artist. */
  error?: ErrorAPIResult['error'];
  retry?: () => void;
  onBack: () => void;
}

/**
 * A song page that cannot open (design m2 note). A song that is not in the catalog of the active
 * artist (a missing or foreign `catalogItemUuid`) shows "This song is not in your catalog."; a
 * failed read shows the standard ErrorView with Retry. Both lead back to Releases.
 */
const ReleaseNotFoundComponent: React.FC<ReleaseNotFoundProps> = ({ error, retry, onBack }) => {
  const intl = useIntl();
  const back = intl.formatMessage(releaseMessages.backToReleases);

  if (error !== undefined) {
    return (
      <Panel pad>
        <ErrorView error={error} retry={retry} />
        <div className="d-flex justify-content-center">
          <SMLButton onClick={onBack}>{back}</SMLButton>
        </div>
      </Panel>
    );
  }

  return (
    <Panel>
      <SMLEmptyState
        icon={faRecordVinyl}
        title={intl.formatMessage(releaseMessages.notFoundTitle)}
        description={intl.formatMessage(releaseMessages.notFoundBody)}
        action={{ label: back, onClick: onBack }}
      />
    </Panel>
  );
};

export const ReleaseNotFound = TypedMemo(ReleaseNotFoundComponent);
